import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import HeroSection from '../components/sections/HeroSection';
import AboutPreview from '../components/AboutPreview';
import ServicesSection from '../components/ServicesSection';
import ContactCTA from '../components/ContactCTA';
import InfiniteMarquee from '../components/animations/InfiniteMarquee';
import Magnetic from '../components/animations/Magnetic';
import SpotlightCard from '../components/animations/SpotlightCard';
import { marqueeText } from '../data/mock';
import { Users, Award, Clock, Target, Shield, BarChart, ArrowRight } from 'lucide-react';
import './Home.css';

gsap.registerPlugin(ScrollTrigger);

// ── Animated counter hook ──────────────────────────────────────────────────
const useAnimatedCounter = (target, duration = 2, shouldStart = false) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!shouldStart) return;
    let start = 0;
    const step = target / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setDisplay(target); clearInterval(timer); }
      else setDisplay(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [shouldStart, target, duration]);
  return display;
};

// ── WhyChooseCard ──────────────────────────────────────────────────────────
const WhyChooseCard = ({ feature, index }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);
  const Icon = feature.icon;

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <motion.div
      ref={cardRef}
      className="why-choose-card"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileHover={{ y: -6 }}
    >
      <div
        className="why-choose-spotlight"
        style={{
          background: isHovered
            ? `radial-gradient(400px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(250,255,0,0.2), transparent 40%)`
            : 'none',
        }}
      />
      <div className="why-choose-icon-wrapper">
        <Icon size={26} />
      </div>
      <h3 className="why-choose-card-title">{feature.title}</h3>
      <p className="why-choose-card-description">{feature.description}</p>
    </motion.div>
  );
};

// ── ProjectCard ────────────────────────────────────────────────────────────
const ProjectCard = ({ project, index }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <motion.div
      ref={cardRef}
      className="project-card"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: 'easeOut' }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <a
        href={project.url || '#'}
        target={project.url ? '_blank' : undefined}
        rel={project.url ? 'noreferrer' : undefined}
        className="project-card-link"
      >
        <div
          className="project-spotlight"
        style={{
          background: isHovered
            ? `radial-gradient(500px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(250,255,0,0.12), transparent 40%)`
            : 'none',
        }}
      />
      <div className="project-card-inner">
        <div className="project-number-large">{project.number}</div>
        <div className="project-meta">
          <span className="project-category">{project.tags[0]}</span>
        </div>
        <h3 className="project-title">{project.title}</h3>
        <p className="project-description">{project.description}</p>
        <div className="project-footer">
          <div className="project-tags">
            {project.tags.map((tag, idx) => (
              <span key={idx} className="project-tag">{tag}</span>
            ))}
          </div>
          <div className="project-arrow-btn">
            <ArrowRight size={18} />
          </div>
        </div>
      </div>
      </a>
    </motion.div>
  );
};

// ── StatCard with flip + counter animation ─────────────────────────────────
const StatCard = ({ stat, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const count = useAnimatedCounter(stat.value, 2, isInView);
  const Icon = stat.icon;

  return (  
    <motion.div
      ref={ref}
      initial={{ opacity: 0, rotateX: -25, y: 60 }}
      animate={isInView ? { opacity: 1, rotateX: 0, y: 0 } : {}}
      transition={{ duration: 0.75, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
    >
      <SpotlightCard className={`stat-card stat-card-${stat.color}`}>
        <div className={`stat-gradient ${stat.gradient}`} />
        <div className="stat-content">
          <div className="stat-icon-wrapper">
            <Icon size={26} />
          </div>
          <div className="stat-value">{count}{stat.suffix}</div>
          <div className="stat-label">{stat.label}</div>
          <div className="stat-description">{stat.description}</div>
          <div className="stat-bar">
            <motion.div
              className="stat-bar-fill"
              initial={{ width: 0 }}
              animate={isInView ? { width: `${stat.percent}%` } : {}}
              transition={{ duration: 1.4, delay: index * 0.15 + 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>
      </SpotlightCard>
    </motion.div>
  );
};

// ── Data ───────────────────────────────────────────────────────────────────
const FEATURED_PROJECTS = [
  {
    id: 1,
    number: "01",
    title: "Apex Gym — Single Scroll Website",
    description: "Apex Gym demo website built as a basic single-scroll landing experience using HTML, CSS, and JavaScript.",
    tags: ["HTML", "CSS", "JavaScript", "Basic Website"],
    url: "https://gym123456.netlify.app/"
  },
  {
    id: 2,
    number: "02",
    title: "Lumio — Agency Landing Page",
    description: "A clean, high-converting landing page for a creative agency. Pixel-perfect layout with smooth scroll animations, mobile-first design, and a strong visual identity.",
    tags: ["Frontend", "React", "Framer Motion", "Basic Plan"]
  },
  {
    id: 3,
    number: "03",
    title: "Cravio — Food Ordering App",
    description: "A full-stack food ordering platform for restaurants and cloud kitchens with real-time order tracking, online payments, and a full admin dashboard.",
    tags: ["Full Stack", "React", "Node.js", "Real-time", "Payments"]
  }
];

const STATS = [
  {
    value: 25, suffix: "+",
    label: "Projects Delivered",
    description: "Successfully shipped & deployed",
    color: "blue", percent: 80,
    gradient: "from-blue-500 via-cyan-400 to-sky-500",
    icon: Award,
  },
  {
    value: 1, suffix: " yr",
    label: "Years of Excellence",
    description: "Building with precision & speed",
    color: "orange", percent: 35,
    gradient: "from-orange-500 via-amber-400 to-yellow-500",
    icon: Clock,
  },
  {
    value: 50, suffix: "K+",
    label: "Lines of Code",
    description: "Clean, efficient, maintainable",
    color: "purple", percent: 92,
    gradient: "from-purple-500 via-violet-400 to-fuchsia-500",
    icon: BarChart,
  }
];

const WHY_FEATURES = [
  { icon: Users,   title: "Small Team, Big Output",   description: "Direct access to the people building your product — no account managers, no middlemen." },
  { icon: Award,   title: "Quality Over Quantity",    description: "We take on select projects and go deep. Every detail gets the attention it deserves." },
  { icon: Clock,   title: "On-Time, Every Time",      description: "Strict milestones and daily updates. You'll never wonder where your project stands." },
  { icon: Target,  title: "Outcome-Driven Builds",    description: "We build for results — conversions, signups, revenue — not just beautiful screens." },
  { icon: Shield,  title: "Transparent Pricing",      description: "Fixed-price packages, 50% upfront. No hidden costs, no surprise invoices." },
  { icon: BarChart,title: "Performance Obsessed",     description: "Sub-3s load times, mobile-first, SEO-ready. Fast sites win more business." },
];

// ── Home ───────────────────────────────────────────────────────────────────
const Home = () => {
  const homeRef = useRef(null);

  useEffect(() => {
    return () => { ScrollTrigger.getAll().forEach(t => t.kill()); };
  }, []);

  return (
    <motion.div
      ref={homeRef}
      className="home-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <HeroSection />
      <InfiniteMarquee items={marqueeText} speed={30} />
      <AboutPreview />
      <ServicesSection />

      {/* Featured Projects */}
      <section className="featured-projects-section" id="projects-section">
        <div className="projects-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {/* <span className="section-eyebrow">Selected Work</span> */}
            <h2 className="section-title">Our Projects</h2>
            <p className="section-subtitle">Real problems. Real solutions. Built to perform.</p>
          </motion.div>

          <div className="projects-grid">
            {FEATURED_PROJECTS.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="home-stats-section">
        <div className="stats-bg-grid" aria-hidden="true" />
        <div className="stats-container">
          <motion.div
            className="stats-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {/* <span className="stats-eyebrow">By the numbers</span> */}
            <h2 className="stats-section-title">Our Impact</h2>
            <p className="stats-section-subtitle">
              Every number represents a real project, a real client, a real result.
            </p>
          </motion.div>

          <div className="stats-grid">
            {STATS.map((stat, index) => (
              <StatCard key={index} stat={stat} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-section">
        <div className="why-choose-container">
          <motion.div
            className="why-choose-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {/* <span className="why-eyebrow">Why TÆKNI</span> */}
            <h2 className="why-choose-title">Why You Choose Us</h2>
            <p className="why-choose-subtitle">
              We're not the biggest agency. We're the most committed one you'll work with.
            </p>
          </motion.div>

          <div className="why-choose-grid">
            {WHY_FEATURES.map((feature, index) => (
              <WhyChooseCard key={index} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      <InfiniteMarquee items={marqueeText} speed={30} />
      <ContactCTA />
    </motion.div>
  );
};

export default Home;